import React from 'react';
import { cn } from '@/lib/utils';
import SiteContainer from './SiteContainer';

/**
 * Section tones. `paper` is transparent: it sits on the page canvas, which paints the
 * paper + grid (`.site-paper` on the page wrapper). `navy` marks itself `.site-dark`
 * (focus rings turn peach); every light tone marks itself `.site-light` (focus rings
 * turn rust, ≥3.9:1 on each of them; the orange default is below 3:1 on the tints).
 * Text on the tinted tones is navy; muted text (brand-muted) passes AA on paper, sheet,
 * sage and butter only (not lilac 4.46:1, not peach 3.86:1).
 */
export const SITE_SECTION_TONES = {
  paper: 'site-light text-navy',
  sheet: 'site-light bg-brand-sheet text-navy',
  navy: 'site-dark bg-navy text-brand-sheet',
  peach: 'site-light bg-brand-peach text-navy',
  sage: 'site-light bg-brand-sage text-navy',
  lilac: 'site-light bg-brand-lilac text-navy',
  butter: 'site-light bg-brand-butter text-navy',
};

/**
 * A page band of the redesign: section rhythm (clamp(64px, 8vw, 112px) top and bottom),
 * an inner container capped at the 1440px canvas with the page gutter.
 *
 * Props:
 * - `tone`: 'paper' (default) | 'sheet' | 'navy' | 'peach' | 'sage' | 'lilac' | 'butter'
 * - `as`: element (default 'section'); `id`
 * - `aria-labelledby` (and any other aria-/data- prop) passes through: name a `section`
 *   by pointing it at its heading's id.
 * - `width`: inner container width, 'canvas' (default) | 'narrow' | 'prose' | 'full'
 * - `spacing`: 'section' (default, the section rhythm) | 'none' (the caller pads)
 * - `divider`: true = 1px rule line on top (the coloured section breaks)
 * - `inset`: true = inset from the canvas edge (15px, 24px from the nav breakpoint), for
 *   coloured blocks that should not touch the canvas edge
 * - `className` (outer), `containerClassName` (inner), `children`
 */
export default function SiteSection({
  tone = 'paper',
  as: Tag = 'section',
  id,
  width = 'canvas',
  spacing = 'section',
  divider = false,
  inset = false,
  className,
  containerClassName,
  children,
  ...rest
}) {
  const t = Object.prototype.hasOwnProperty.call(SITE_SECTION_TONES, tone) ? tone : 'paper';
  return (
    <Tag
      id={id}
      data-tone={t}
      className={cn(
        SITE_SECTION_TONES[t],
        spacing === 'none' ? null : 'py-section',
        divider && 'border-t border-brand-rule',
        inset && 'mx-[15px] nav:mx-6',
        className,
      )}
      {...rest}
    >
      <SiteContainer width={width} className={containerClassName}>
        {children}
      </SiteContainer>
    </Tag>
  );
}
